import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

export interface OfflineCreditLowered {
  at: string;
  fileId: string;
  windowSeconds?: { from: number, to: number };
  budgetSeconds?: { from: number, to: number };
}

/** A licence file set a longer seat time than the administrator's, and theirs was raised to it. */
export interface SeatTimeRaised {
  at: string;
  fileId: string;
  from: number;
  to: number;
}

export interface License {
  licenseId: string;
  /** Activation id — absent until the administrator assigns one. */
  id?: string;
  label?: string;
  deviceLimitCount: number;
  composerDeviceLimitCount: number;
  applicationFeatures: string[];
  featureDetail?: Record<string, unknown>;
  issueDate: string;
  expirationDate: string;
  /**
   * Seat time from the licence file, in seconds: how long a seat stays with a device that has
   * stopped calling back. The least the administrator's own may be, and read-only here.
   */
  seatAllocationTimeInSeconds: number;
  /** The administrator's own seat time, never below the file's. Absent: the file's applies. */
  seatAllocationAdminTimeInSeconds?: number;
  /** What the server gives a seat now: the administrator's when set, the file's otherwise. */
  effectiveSeatAllocationTimeInSeconds?: number;
  /** Set when a licence file raised the administrator's seat time; cleared when it is next saved. */
  seatAllocationTimeRaised?: SeatTimeRaised;
  /**
   * Offline credit, from the licence file: a window and a budget, in seconds. Zero in either
   * means no credit. The ceiling for the administrator's own values below, and read-only here
   * like every other entitlement.
   */
  offlineCreditWindowSeconds?: number;
  offlineCreditBudgetSeconds?: number;
  /** The administrator's own values, never above the file's. Absent: the file's value applies. */
  offlineCreditAdminWindowSeconds?: number;
  offlineCreditAdminBudgetSeconds?: number;
  /** What the server issues: the lower of the admin value and the file's. */
  effectiveOfflineCreditWindowSeconds?: number;
  effectiveOfflineCreditBudgetSeconds?: number;
  /** Set when a licence file lowered the admin values; cleared when they are next saved. */
  offlineCreditLowered?: OfflineCreditLowered;
  sourceFileId: string;
  /** False until the administrator sets a password; the license serves no seats. */
  passwordSet: boolean;
  locked: boolean;
  isFloating: boolean;
  /** Seats still held, counted per application family. */
  usedDeviceCount: number;
  usedComposerDeviceCount: number;
  /**
   * Seat rows that have gone quiet past every window. They are free for the next client that
   * asks, so they are not "in use", but nothing sweeps them in the background either, which is
   * why they are reported rather than simply ignored.
   */
  reclaimableDeviceCount: number;
  reclaimableComposerDeviceCount: number;
  /** Whole days until `expirationDate` on the server's clock; zero or less once it has passed. */
  expiresInDays: number;
  /** 60, 30, 14 or 7 from 60 days before expiry: the reminder point most recently crossed. */
  expiryReminderDays?: number;
  /** Whether a day of the last 30 (UTC) reached or exceeded the seats, from the seat usage history. */
  seatUsage30d: { atLimit: boolean, exceeded: boolean };
  createdAt: string;
  updatedAt: string;
}

export const licenseApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLicenses: builder.query<License[], void>({
      query: () => '/v1/licenses',
      providesTags: ['License'],
      onQueryStarted: handleApiResult(),
    }),
    // Seat counts and features are entitlements and are not editable here by design. The offline
    // credit values are the administrator's own, below the file's, and the seat time their own,
    // above the file's; null clears one.
    updateLicense: builder.mutation<License, {
      licenseId: string,
      label?: string,
      locked?: boolean,
      offlineCreditAdminWindowSeconds?: number | null,
      offlineCreditAdminBudgetSeconds?: number | null,
      seatAllocationAdminTimeInSeconds?: number | null
    }>({
      query: ({ licenseId, ...body }) => ({ url: `/v1/licenses/${licenseId}`, method: 'PATCH', body }),
      invalidatesTags: ['License'],
      onQueryStarted: handleApiResult('License updated.'),
    }),
    setActivationId: builder.mutation<License, { licenseId: string, id: string }>({
      query: ({ licenseId, id }) => ({
        url: `/v1/licenses/${licenseId}/activation-id`, method: 'PUT', body: { id }
      }),
      invalidatesTags: ['License', 'Installation'],
      onQueryStarted: handleApiResult('Activation id set.'),
    }),
    setLicensePassword: builder.mutation<void, { licenseId: string, password: string }>({
      query: ({ licenseId, password }) => ({
        url: `/v1/licenses/${licenseId}/password`, method: 'PUT', body: { password }
      }),
      invalidatesTags: ['License', 'Installation'],
      onQueryStarted: handleApiResult('License password set.'),
    }),
  }),
});

export const {
  useGetLicensesQuery,
  useUpdateLicenseMutation,
  useSetActivationIdMutation,
  useSetLicensePasswordMutation,
} = licenseApiSlice;
